/**
 * Wrapper fetch typé pour appeler l'API Le Comptoir.
 * - Cookies de session envoyés automatiquement (credentials: 'include').
 * - Gestion uniforme des erreurs métier (format ApiError).
 * - Sur 401 (UNAUTHENTICATED / SESSION_INVALID), redirige vers /connexion
 *   automatiquement (sauf si on est déjà sur /connexion ou /api/auth/me).
 */
import type { ApiError } from '@lecomptoir/shared';

const BASE = import.meta.env.VITE_API_URL ?? '/api';

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

// Évite les boucles : ne redirige pas si on est déjà sur /connexion ou si la
// requête concerne /auth/me (qui peut légitimement retourner 401 quand on
// n'est pas connecté, sans qu'il soit utile de rediriger).
function shouldRedirectOn401(path: string): boolean {
  if (typeof window === 'undefined') return false;
  if (path.startsWith('/auth/')) return false;
  const here = window.location.pathname;
  if (here.startsWith('/connexion') || here === '/' || here.startsWith('/invitation')) return false;
  return true;
}

/**
 * Clé sessionStorage utilisée pour stocker le token d'impersonation.
 * sessionStorage est per-onglet : l'onglet admin et l'onglet observateur
 * sont isolés, même si le cookie de session est partagé.
 */
export const IMPERSONATE_STORAGE_KEY = 'lecomptoir:impersonate-token';

/**
 * Extrait les erreurs de champ depuis une ApiClientError de type VALIDATION_ERROR.
 * Retourne un dictionnaire { nomDuChamp: premierMessage }.
 */
export function extractFieldErrors(err: unknown): Record<string, string> {
  if (!(err instanceof ApiClientError)) return {};
  if (err.code !== 'VALIDATION_ERROR') return {};
  const details = err.details as { fieldErrors?: Record<string, string[]> } | undefined;
  if (!details?.fieldErrors) return {};
  return Object.fromEntries(
    Object.entries(details.fieldErrors)
      .filter(([, msgs]) => msgs.length > 0)
      .map(([key, msgs]) => [key, msgs[0]!]),
  );
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Mode observateur : si un token est stocké dans sessionStorage pour cet
  // onglet, on l'ajoute à chaque requête via le header x-impersonate-token.
  const impersonateToken = sessionStorage.getItem(IMPERSONATE_STORAGE_KEY);

  const extraHeaders: Record<string, string> = impersonateToken
    ? { 'x-impersonate-token': impersonateToken }
    : {};

  const res = await fetch(`${BASE}${path}`, {
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      ...extraHeaders,
      ...(init.headers ?? {}),
    },
    ...init,
  });

  const text = await res.text();
  const json = text ? (JSON.parse(text) as { data?: T } | ApiError) : null;

  if (!res.ok) {
    const err = (json as ApiError | null)?.error;
    if (
      res.status === 401 &&
      (err?.code === 'UNAUTHENTICATED' || err?.code === 'SESSION_INVALID') &&
      shouldRedirectOn401(path)
    ) {
      // Petit délai pour laisser React Query nettoyer son cache
      setTimeout(() => {
        window.location.href = `/connexion?reason=session_expired&from=${encodeURIComponent(window.location.pathname)}`;
      }, 50);
    }
    // Token d'impersonation expiré : le purger immédiatement pour débloquer
    // toutes les requêtes suivantes (le token est envoyé sur chaque requête).
    if (res.status === 403 && err?.code === 'INVALID_IMPERSONATE_TOKEN') {
      sessionStorage.removeItem(IMPERSONATE_STORAGE_KEY);
      setTimeout(() => {
        window.location.href = '/admin';
      }, 50);
    }
    throw new ApiClientError(
      res.status,
      err?.code ?? 'UNKNOWN_ERROR',
      err?.message ?? `Erreur ${res.status}`,
      err?.details,
    );
  }
  return (json as { data: T }).data;
}
